import React from 'react';
import PlacesAutocomplete from '../../../UI/PlacesAutocomplete/PlacesAutocomplete.tsx';
import type { GeocoderAddressComponent } from '../../../UI/PlacesAutocomplete/PlacesAutocomplete.tsx';
import { JURISDICTIONS } from '../../../../constants/jurisdictions.ts';
import {
  COMMERCE_DELIVERY_RADIUS_KM_MIN,
  COMMERCE_DELIVERY_RADIUS_UI_KM_MAX,
  COMMERCE_LISTED_ZONES_ID_MAX,
  type CommerceDeliveryScope,
  type CommerceDepartmentCity,
} from '../../../../services/commerceService.ts';
import CommerceDeliveryMap from './CommerceDeliveryMap.tsx';
import CommerceListedZonesMap from './CommerceListedZonesMap.tsx';
import {
  ZONE_INFLUENCE_PRESET_KM,
  RADIUS_SLIDER_STEPS,
  clampRadiusKm,
  kmFromSliderPosition,
  sliderPositionFromKm,
} from './commerceFormRadiusUtils.ts';
import type { ListedZonesState } from './commerceFormListedZonesReducer.ts';

export type ListedZonesGroupRow = {
  jurisdictionId: number;
  provinceName: string;
  ids: string[];
};

export interface CommerceFormDeliverySectionProps {
  styles: Record<string, string>;
  disabled: boolean;
  isFieldInvalid: (fieldId: string) => boolean;
  deliveryScope: CommerceDeliveryScope;
  setDeliveryScope: (s: CommerceDeliveryScope) => void;
  deliveryRadiusKm: number;
  setDeliveryRadiusKm: (km: number) => void;
  mapLat: number | null;
  mapLng: number | null;
  onMapPositionChange: (lat: number, lng: number) => void;
  placeInput: string;
  setPlaceInput: (v: string) => void;
  onPlaceSelect: (place: {
    address: string;
    lat: number;
    lng: number;
    addressComponents?: GeocoderAddressComponent[];
  }) => void;
  listedZones: ListedZonesState;
  jurisdiccionId: number | '';
  setJurisdiccionId: (j: number | '') => void;
  departments: CommerceDepartmentCity[];
  departmentsLoading: boolean;
  departmentsError: string;
  listedZonesSearch: string;
  setListedZonesSearch: (v: string) => void;
  filteredDepartments: CommerceDepartmentCity[];
  listedZonesGrouped: ListedZonesGroupRow[];
  toggleListedZone: (id: string, name?: string) => void;
  removeListedZone: (id: string) => void;
}

const CommerceFormDeliverySection: React.FC<CommerceFormDeliverySectionProps> = ({
  styles,
  disabled,
  isFieldInvalid,
  deliveryScope,
  setDeliveryScope,
  deliveryRadiusKm,
  setDeliveryRadiusKm,
  mapLat,
  mapLng,
  onMapPositionChange,
  placeInput,
  setPlaceInput,
  onPlaceSelect,
  listedZones,
  jurisdiccionId,
  setJurisdiccionId,
  departments,
  departmentsLoading,
  departmentsError,
  listedZonesSearch,
  setListedZonesSearch,
  filteredDepartments,
  listedZonesGrouped,
  toggleListedZone,
  removeListedZone,
}) => {
  return (
    <section
      id="commerce-delivery-focus"
      tabIndex={-1}
      className={`${styles.deliverySection} ${isFieldInvalid('commerce-delivery-focus') ? styles.deliverySectionInvalid : ''}`}
      aria-labelledby="commerce-delivery-heading"
    >
      <h3 id="commerce-delivery-heading" className={styles.deliveryTitle}>
        Cómo comercializás tus productos
      </h3>
      <p className={styles.deliveryHint}>
        Elegí primero una modalidad de cobertura. Después te mostramos solo los campos necesarios para configurarla.
      </p>
      {isFieldInvalid('commerce-delivery-focus') ? (
        <p className={styles.fieldInvalidHintBlock}>Revisá modalidad, provincia o cobertura en esta sección.</p>
      ) : null}
      <div className={styles.scopeRow}>
        <button
          type="button"
          className={`${styles.scopeBtn} ${deliveryScope === 'LOCAL' ? styles.scopeBtnActive : ''}`}
          disabled={disabled}
          onClick={() => setDeliveryScope('LOCAL')}
        >
          Radio alrededor de un punto (mapa)
        </button>
        <button
          type="button"
          className={`${styles.scopeBtn} ${deliveryScope === 'LISTED_ZONES' ? styles.scopeBtnActive : ''}`}
          disabled={disabled}
          onClick={() => setDeliveryScope('LISTED_ZONES')}
        >
          Zonas seleccionadas
        </button>
        <button
          type="button"
          className={`${styles.scopeBtn} ${deliveryScope === 'NATIONWIDE' ? styles.scopeBtnActive : ''}`}
          disabled={disabled}
          onClick={() => setDeliveryScope('NATIONWIDE')}
        >
          Todo el país
        </button>
      </div>
      <p className={styles.deliverySummary}>
        {deliveryScope === 'LOCAL'
          ? `Cobertura por radio de influencia: ${deliveryRadiusKm} km desde un punto en el mapa.`
          : deliveryScope === 'LISTED_ZONES'
            ? `Cobertura por zonas del listado: ${listedZones.ids.length} seleccionadas.`
            : 'Cobertura nacional en todo el país.'}
      </p>
      <div className={styles.deliveryMapRow}>
        <div className={styles.deliveryMapColMap}>
          {deliveryScope === 'LOCAL' ? (
            <CommerceDeliveryMap
              lat={mapLat}
              lng={mapLng}
              radiusKm={deliveryRadiusKm}
              onPositionChange={onMapPositionChange}
            />
          ) : deliveryScope === 'LISTED_ZONES' ? (
            jurisdiccionId === '' ? (
              <div className={styles.deliveryListedMapPlaceholder}>
                <span className={styles.deliveryListedMapLabel}>
                  Elegí primero la provincia en el panel de la derecha; después vas a ver el mapa con pins en las zonas
                  que marques.
                </span>
              </div>
            ) : (
              <div className={styles.deliveryMapLoadingWrap}>
                <CommerceListedZonesMap
                  jurisdictionName={JURISDICTIONS.find((j) => j.id === jurisdiccionId)?.name ?? ''}
                  cities={departments}
                  selectedIds={listedZones.ids}
                  selectedLabelsJson={JSON.stringify(listedZones.labels)}
                />
                {departmentsLoading ? (
                  <div className={styles.deliveryMapLoadingOverlay} role="status" aria-live="polite">
                    <span className={styles.deliveryMapLoadingSpinner} aria-hidden />
                    <span className={styles.deliveryMapLoadingText}>
                      Cargando zonas de {JURISDICTIONS.find((j) => j.id === jurisdiccionId)?.name ?? 'la provincia'}...
                    </span>
                  </div>
                ) : null}
              </div>
            )
          ) : (
            <div className={styles.deliveryNationwideMapPlaceholder} aria-hidden>
              <span className={styles.deliveryNationwideMapLabel}>Cobertura nacional</span>
            </div>
          )}
        </div>
        <aside className={styles.deliveryMapColPanel}>
          {deliveryScope === 'LOCAL' ? (
            <>
              <p className={styles.deliveryPanelTitle}>Punto de referencia</p>
              <p className={styles.mapHint}>
                Elegí un resultado del buscador o tocá el mapa: el texto y el pin se sincronizan con el punto del
                centro.
              </p>
              <div className={styles.field}>
                <PlacesAutocomplete
                  inputId="commerce-place-local"
                  inputAriaLabel="Buscar dirección o localidad para centrar el mapa"
                  leadingSearchIcon
                  searchIconPosition="end"
                  value={placeInput}
                  onChange={setPlaceInput}
                  onPlaceSelect={onPlaceSelect}
                  placeholder="Buscá dirección, barrio o localidad…"
                  hideLabel
                  outlineError={isFieldInvalid('commerce-place-local')}
                />
                {isFieldInvalid('commerce-place-local') ? (
                  <span className={styles.fieldInvalidHint}>
                    Elegí un resultado del buscador o ubicá el pin en el mapa.
                  </span>
                ) : null}
              </div>
              <p className={styles.deliveryPanelTitle}>Centro del mapa</p>
              <p className={styles.mapHint}>
                Mové el pin o tocá el mapa; la dirección del buscador se actualiza con ese lugar cuando hay datos de
                mapa disponibles.
              </p>
              <p className={styles.influenceSubheading} id="commerce-influence-presets-heading">
                Tamaño de la zona de influencia
              </p>
              <p className={styles.zoneHint}>
                El deslizador prioriza radios chicos para afinar mejor; para un valor exacto grande usá el número (1 a{' '}
                {COMMERCE_DELIVERY_RADIUS_UI_KM_MAX} km). El círculo en el mapa muestra la zona real.
              </p>
              <div
                className={styles.radiusChips}
                role="group"
                aria-labelledby="commerce-influence-presets-heading"
              >
                {ZONE_INFLUENCE_PRESET_KM.map((km) => (
                  <button
                    key={km}
                    type="button"
                    className={`${styles.radiusChip} ${deliveryRadiusKm === km ? styles.radiusChipActive : ''}`}
                    disabled={disabled}
                    onClick={() => setDeliveryRadiusKm(km)}
                    aria-label={`${km} kilómetros`}
                  >
                    <span className={styles.radiusChipKm}>{km} km</span>
                  </button>
                ))}
              </div>
              <div
                className={`${styles.radiusRow} ${isFieldInvalid('commerce-radius-km-num') ? styles.fieldRowInvalid : ''}`}
              >
                <div className={styles.radiusLabelRow}>
                  <label
                    className={`${styles.radiusLabel} ${isFieldInvalid('commerce-radius-km-num') ? styles.fieldInvalidLabel : ''}`}
                    htmlFor="commerce-radius-km"
                  >
                    Afinar alcance (km)
                  </label>
                  <div className={styles.radiusValueRow}>
                    <input
                      id="commerce-radius-km-num"
                      type="number"
                      className={`${styles.radiusNumber} ${isFieldInvalid('commerce-radius-km-num') ? styles.radiusNumberInvalid : ''}`}
                      min={COMMERCE_DELIVERY_RADIUS_KM_MIN}
                      max={COMMERCE_DELIVERY_RADIUS_UI_KM_MAX}
                      step={1}
                      disabled={disabled}
                      value={deliveryRadiusKm}
                      onChange={(e) => {
                        const v = e.target.valueAsNumber;
                        if (!Number.isFinite(v)) return;
                        setDeliveryRadiusKm(clampRadiusKm(v));
                      }}
                      aria-label="Kilómetros de alcance de la zona de influencia"
                      aria-invalid={isFieldInvalid('commerce-radius-km-num')}
                    />
                    <span className={styles.radiusValue}>km</span>
                  </div>
                </div>
                <input
                  id="commerce-radius-km"
                  type="range"
                  className={styles.radiusSlider}
                  min={0}
                  max={RADIUS_SLIDER_STEPS}
                  step={1}
                  value={sliderPositionFromKm(deliveryRadiusKm)}
                  disabled={disabled}
                  aria-valuetext={`${deliveryRadiusKm} kilómetros`}
                  onChange={(e) => setDeliveryRadiusKm(kmFromSliderPosition(Number(e.target.value)))}
                />
                {isFieldInvalid('commerce-radius-km-num') ? (
                  <span className={styles.fieldInvalidHint}>Ajustá el alcance en el rango permitido.</span>
                ) : null}
              </div>
            </>
          ) : deliveryScope === 'LISTED_ZONES' ? (
            <>
              <p className={styles.deliveryPanelTitle}>Zonas del listado</p>
              <p className={styles.listedStepBodyHint}>
                Elegí una provincia y marcá zonas; podés cambiar de provincia para sumar otras. Cada zona queda asociada
                a la provincia del listado en la que la marcaste.
              </p>
              <label
                className={`${styles.radiusLabel} ${isFieldInvalid('commerce-jur') ? styles.fieldInvalidLabel : ''}`}
                htmlFor="commerce-jur"
              >
                Provincia
              </label>
              <select
                id="commerce-jur"
                className={`${styles.listedJurSelect} ${isFieldInvalid('commerce-jur') ? styles.listedJurSelectInvalid : ''}`}
                value={jurisdiccionId === '' ? '' : String(jurisdiccionId)}
                disabled={disabled}
                onChange={(e) => setJurisdiccionId(e.target.value ? Number(e.target.value) : '')}
                aria-labelledby="commerce-listed-jur-heading"
                aria-invalid={isFieldInvalid('commerce-jur')}
              >
                <option value="">Elegí provincia</option>
                {JURISDICTIONS.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.name}
                  </option>
                ))}
              </select>
              {isFieldInvalid('commerce-jur') ? (
                <span className={styles.fieldInvalidHint}>Completá este dato</span>
              ) : null}
              {jurisdiccionId === '' ? (
                <p className={styles.listedZonesStatus}>Cuando elijas provincia, acá aparecerá el listado y el mapa.</p>
              ) : (
                <>
                  {departmentsLoading ? <p className={styles.listedZonesStatus}>Cargando zonas…</p> : null}
                  {departmentsError ? <p className={styles.listedZonesError}>{departmentsError}</p> : null}
                  {!departmentsLoading && !departmentsError && departments.length === 0 ? (
                    <p className={styles.listedZonesStatus}>No hay zonas para esta provincia.</p>
                  ) : null}
                  <label
                    className={`${styles.radiusLabel} ${isFieldInvalid('commerce-listed-zones-search') ? styles.fieldInvalidLabel : ''}`}
                    htmlFor="commerce-listed-zones-search"
                  >
                    Buscar en el listado
                  </label>
                  <input
                    id="commerce-listed-zones-search"
                    type="search"
                    className={`${styles.listedZonesSearch} ${isFieldInvalid('commerce-listed-zones-search') ? styles.listedZonesSearchInvalid : ''}`}
                    value={listedZonesSearch}
                    disabled={disabled || departmentsLoading || departments.length === 0}
                    onChange={(e) => setListedZonesSearch(e.target.value)}
                    placeholder="Nombre de partido o localidad"
                    autoComplete="off"
                    aria-labelledby="commerce-listed-zones-step2"
                    aria-invalid={isFieldInvalid('commerce-listed-zones-search')}
                  />
                  {isFieldInvalid('commerce-listed-zones-search') ? (
                    <span className={styles.fieldInvalidHint}>Elegí al menos una zona del listado.</span>
                  ) : null}
                  <p className={styles.listedZonesMeta}>
                    Seleccionadas: {listedZones.ids.length} / {COMMERCE_LISTED_ZONES_ID_MAX}
                  </p>
                  {listedZones.ids.length > 0 ? (
                    <div className={styles.listedChipsWrap}>
                      <p className={styles.listedChipsLabel}>Elegidas</p>
                      <div className={styles.listedChipsGroups} role="list" aria-label="Zonas elegidas por provincia">
                        {listedZonesGrouped.map((group) => (
                          <div key={group.jurisdictionId} className={styles.listedChipsGroup}>
                            <p className={styles.listedChipsGroupTitle}>{group.provinceName}</p>
                            <div className={styles.listedChipsRow}>
                              {group.ids.map((id) => {
                                const zoneLabel = listedZones.labels[id] ?? id;
                                const fullLabel =
                                  group.jurisdictionId === -1
                                    ? zoneLabel
                                    : `${zoneLabel} · ${group.provinceName}`;
                                return (
                                  <span key={id} className={styles.listedChip} role="listitem">
                                    <span className={styles.listedChipText} title={fullLabel}>
                                      {zoneLabel}
                                    </span>
                                    <button
                                      type="button"
                                      className={styles.listedChipRemove}
                                      disabled={disabled}
                                      aria-label={`Quitar ${fullLabel}`}
                                      onClick={() => removeListedZone(id)}
                                    >
                                      ×
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  <div className={styles.listedZonesList} role="group" aria-label="Zonas de cobertura por listado">
                    {filteredDepartments.map((c) => (
                      <label key={c.id} className={styles.listedZoneRow}>
                        <input
                          type="checkbox"
                          checked={listedZones.ids.includes(c.id)}
                          disabled={
                            disabled ||
                            (!listedZones.ids.includes(c.id) &&
                              listedZones.ids.length >= COMMERCE_LISTED_ZONES_ID_MAX)
                          }
                          onChange={() => toggleListedZone(c.id, c.name)}
                        />
                        <span className={styles.listedZoneName}>{c.name}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <p className={styles.deliveryPanelTitle}>Todo el país</p>
              <p className={styles.mapHint}>
                No se usa punto ni zona de influencia en el mapa: queda alcance nacional. Podés volver a &quot;Zona de
                influencia (mapa)&quot; o a &quot;Zonas del listado&quot; para acotar.
              </p>
            </>
          )}
        </aside>
      </div>
    </section>
  );
};

export default CommerceFormDeliverySection;
